import React from 'react';
import { Box, Container, Typography, Link } from '@mui/material';
import { motion } from 'framer-motion';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import InstagramIcon from '@mui/icons-material/Instagram';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { colors } from '../theme';
import { fadeInUp, staggerContainer } from '../utils/animations';

/**
 * HEURÍSTICAS APLICADAS NO FOOTER:
 *
 * #1 - Visibilidade do status do sistema:
 *   - Links de contato claramente visíveis
 *   - Feedback hover em todos os links
 *
 * #2 - Correspondência entre sistema e mundo real:
 *   - Ícones familiares de redes sociais
 *   - Links de navegação consistentes com header
 *
 * #4 - Consistência e padrões:
 *   - Mesma navegação do header
 *   - Cores e tipografia consistentes
 *
 * #7 - Flexibilidade e eficiência de uso:
 *   - Acesso rápido a todas as seções
 *   - Múltiplas formas de contato
 */

const navLinks = [
  { label: 'Início', href: '#inicio' },
  { label: 'Produtos', href: '#produtos' },
  { label: 'Sobre', href: '#sobre' },
  { label: 'Parceiros', href: '#parceiros' },
];

const Footer: React.FC = () => {
  const handleNavClick = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box
      id="contato"
      component="footer"
      sx={{
        backgroundColor: colors.blackMatte,
        borderTop: `3px solid ${colors.agedGold}`,
        py: { xs: 6, md: 8 },
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: 'wrap',
              gap: { xs: 4, md: 6 },
            }}
          >
            {/* Coluna 1: Logo e Redes Sociais */}
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 33%' } }}>
              <motion.div variants={fadeInUp}>
                <Box
                  component={motion.img}
                  whileHover={{ scale: 1.05 }}
                  src="/vertcial_brand.svg"
                  alt="Barba Barão"
                  sx={{
                    height: '120px',
                    width: 'auto',
                    mb: 2,
                    // Filtro para cor bege/dourada (#C9B18C)
                    filter: 'brightness(0) saturate(100%) invert(71%) sepia(21%) saturate(471%) hue-rotate(359deg) brightness(96%) contrast(87%)',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
                  }}
                  onClick={() => handleNavClick('#inicio')}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.beige,
                    mb: 3,
                    opacity: 0.9,
                    lineHeight: 1.7,
                  }}
                >
                  Sem frescura. Só cuidado de respeito.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Link
                    component={motion.a}
                    whileHover={{ scale: 1.2, y: -3 }}
                    whileTap={{ scale: 0.9 }}
                    href="https://instagram.com/barbabarao"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram da Barba Barão"
                    sx={{
                      color: colors.agedGold,
                      transition: 'color 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
                      '&:hover': {
                        color: colors.beige,
                      },
                    }}
                  >
                    <InstagramIcon sx={{ fontSize: 32 }} />
                  </Link>
                  <Link
                    component={motion.a}
                    whileHover={{ scale: 1.2, y: -3 }}
                    whileTap={{ scale: 0.9 }}
                    href="https://wa.me/5511945018356"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="WhatsApp da Barba Barão"
                    sx={{
                      color: colors.agedGold,
                      transition: 'color 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
                      '&:hover': {
                        color: colors.beige,
                      },
                    }}
                  >
                    <WhatsAppIcon sx={{ fontSize: 32 }} />
                  </Link>
                </Box>
              </motion.div>
            </Box>

            {/* Coluna 2: Navegação */}
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 33%' } }}>
              <motion.div variants={fadeInUp}>
                <Typography
                  variant="h6"
                  sx={{
                    color: colors.agedGold,
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Navegação
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {navLinks.map((link, index) => (
                    <Link
                      key={link.label}
                      component={motion.button}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        delay: index * 0.1,
                        duration: 0.3,
                        ease: [0.43, 0.13, 0.23, 0.96],
                      }}
                      whileHover={{ x: 8 }}
                      onClick={() => handleNavClick(link.href)}
                      sx={{
                        color: colors.beige,
                        textDecoration: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'color 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
                        fontSize: '1rem',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        '&:hover': {
                          color: colors.agedGold,
                        },
                      }}
                    >
                      {link.label}
                    </Link>
                  ))}
                </Box>
              </motion.div>
            </Box>

            {/* Coluna 3: Contato */}
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 33%' } }}>
              <motion.div variants={fadeInUp}>
                <Typography
                  variant="h6"
                  sx={{
                    color: colors.agedGold,
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Contato
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box
                    component={motion.div}
                    whileHover={{ x: 4 }}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                  >
                    <WhatsAppIcon sx={{ color: colors.agedGold, fontSize: 24 }} />
                    <Link
                      href="https://wa.me/5511945018356"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: colors.beige,
                        textDecoration: 'none',
                        transition: 'color 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
                        '&:hover': {
                          color: colors.agedGold,
                        },
                      }}
                    >
                      (11) 94501-8356
                    </Link>
                  </Box>
                  <Box
                    component={motion.div}
                    whileHover={{ x: 4 }}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                  >
                    <EmailIcon sx={{ color: colors.agedGold, fontSize: 24 }} />
                    <Link
                      href="mailto:contato@barbabarao.com.br"
                      sx={{
                        color: colors.beige,
                        textDecoration: 'none',
                        transition: 'color 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
                        '&:hover': {
                          color: colors.agedGold,
                        },
                      }}
                    >
                      contato@barbabarao.com.br
                    </Link>
                  </Box>
                </Box>
              </motion.div>
            </Box>
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{
            delay: 0.4,
            duration: 0.6,
            ease: [0.43, 0.13, 0.23, 0.96],
          }}
        >
          <Box
            sx={{
              borderTop: `1px solid ${colors.agedGold}40`,
              mt: { xs: 5, md: 6 },
              pt: 4,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: colors.beige,
                mb: 2,
                opacity: 0.8,
              }}
            >
              © {new Date().getFullYear()} Barba Barão. Todos os direitos reservados.
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: colors.beige,
                  opacity: 0.9,
                }}
              >
                Desenvolvido com
              </Typography>
              <FavoriteIcon
                component={motion.svg}
                animate={{
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  repeatType: 'reverse',
                }}
                sx={{
                  color: colors.darkWine,
                  fontSize: 16,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: colors.beige,
                  opacity: 0.9,
                }}
              >
                pela
              </Typography>
              <Link
                component={motion.a}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                href="https://www.oitu.solutions/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: colors.agedGold,
                  textDecoration: 'none',
                  fontWeight: 600,
                  transition: 'color 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
                  '&:hover': {
                    color: colors.beige,
                    textDecoration: 'underline',
                  },
                }}
              >
                oitu
              </Link>
            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Footer;
